/**
 * Candidate triggers for the issue #249 frame displacement.
 *
 * What the on-device measurements established (do not re-derive these):
 *   - Chrome sizes the viewport CORRECTLY and positions the web-view frame
 *     ~121pt too high, under its own top toolbar.
 *   - The displacement is invisible to the page: safe-area insets, screenY,
 *     innerHeight, clientHeight, visualViewport and every rect read identical
 *     in the broken and healthy states. So it cannot be detected, only avoided.
 *   - A second navigation at boot does NOT clear it (proved: the replayed load
 *     carried ktrl=1 and was still displaced).
 *   - Hiding the tab and re-showing it DOES clear it. The trigger class is a
 *     view RE-ATTACH, not a navigation.
 *
 * Each experiment below is a different way to provoke that re-attach. Only a
 * real phone can judge them — the numbers do not move, the pixels do — so this
 * panel runs them one tap at a time and the operator says which one worked.
 *
 * Opt-in per device with `?ktexp=1`, same as the diagnostic overlay.
 */

export const EXP_PARAM = 'ktexp';
export const EXP_STORAGE_KEY = 'kt_viewport_exp';

const KEYBOARD_HOLD_MS = 700;
const TAB_HOLD_MS = 500;
const FULLSCREEN_HOLD_MS = 400;

/** Force a synchronous layout so a style change cannot be coalesced away. */
function reflow(doc) {
	return doc.documentElement.offsetHeight;
}

/**
 * The candidates, cheapest and least disruptive first. `run` may return a
 * promise; the panel does not care, it only needs the side effect.
 */
export const EXPERIMENTS = [
	{
		key: 'tab',
		// Closest analogue to the manual fix: open a tab (this one hides) and
		// close it (this one is re-shown). Needs the tap as its user gesture.
		run: (w) => {
			const other = w.open('about:blank', '_blank');
			if (!other) return 'blocked';
			w.setTimeout(() => other.close(), TAB_HOLD_MS);
			return 'ok';
		}
	},
	{
		key: 'fullscreen',
		// Chrome iOS enables the Fullscreen API (wk_web_view_configuration_provider
		// calls setElementFullscreenEnabled:YES on iOS 16+), and entering moves the
		// web view into a fullscreen window and back out — a re-attach, which is
		// the trigger class that the manual tab switch proved works. Needs the tap
		// as its user gesture. Raised by the QR-scan session, 2026-09-22.
		run: (w) => {
			const el = w.document.documentElement;
			const enter = el.requestFullscreen || el.webkitRequestFullscreen;
			if (!enter) return 'unsupported';
			const done = () => {
				const exit = w.document.exitFullscreen || w.document.webkitExitFullscreen;
				if (exit) exit.call(w.document);
			};
			const result = enter.call(el);
			if (result && typeof result.catch === 'function') {
				result.catch(() => {});
			}
			w.setTimeout(done, FULLSCREEN_HOLD_MS);
			return 'ok';
		}
	},
	{
		key: 'keyboard',
		// Showing and hiding the keyboard resizes the frame itself, which is the
		// same class of event as a re-attach.
		run: (w) => {
			const doc = w.document;
			const input = doc.createElement('input');
			input.setAttribute('type', 'text');
			const s = input.style;
			s.position = 'fixed';
			s.bottom = '0';
			s.left = '0';
			s.opacity = '0.01';
			s.fontSize = '16px'; // < 16px makes iOS zoom, which is its own bug
			doc.body.appendChild(input);
			input.focus();
			w.setTimeout(() => {
				input.blur();
				input.remove();
			}, KEYBOARD_HOLD_MS);
			return 'ok';
		}
	},
	{
		key: 'reload',
		run: (w) => {
			w.location.reload();
			return 'ok';
		}
	},
	{
		key: 'detach',
		// Take the shell out of the render tree and put it back, forcing a full
		// re-layout of the compositing scene rather than a repaint.
		run: (w) => {
			const app = w.document.getElementById('app');
			if (!app) return 'no-app';
			const prev = app.style.display;
			app.style.display = 'none';
			reflow(w.document);
			app.style.display = prev || '';
			reflow(w.document);
			return 'ok';
		}
	},
	{
		key: 'scroll',
		// Briefly make the document scrollable and scroll it. Chrome's toolbar
		// model ignores non-user scrolls, so this is expected to fail — it is
		// here to confirm that, not because it is promising.
		run: (w) => {
			const doc = w.document;
			const html = doc.documentElement;
			const prev = html.style.overflow;
			const spacer = doc.createElement('div');
			spacer.style.height = '200vh';
			doc.body.appendChild(spacer);
			html.style.overflow = 'auto';
			reflow(doc);
			w.scrollTo(0, 2);
			w.setTimeout(() => {
				w.scrollTo(0, 0);
				html.style.overflow = prev;
				spacer.remove();
			}, 250);
			return 'ok';
		}
	},
	{
		key: 'viewport',
		// Rewrite the viewport meta, which is the one input that can make WebKit
		// recompute the visual viewport from scratch.
		run: (w) => {
			const meta = w.document.querySelector('meta[name="viewport"]');
			if (!meta) return 'no-meta';
			const original = meta.getAttribute('content');
			meta.setAttribute('content', `${original},viewport-fit=cover`);
			reflow(w.document);
			w.setTimeout(() => meta.setAttribute('content', original), 150);
			return 'ok';
		}
	}
];

/** Look up an experiment by key. */
export function getExperiment(key) {
	return EXPERIMENTS.find((e) => e.key === key) || null;
}

/** `?ktexp=1` → 'on', `?ktexp=0` → 'off', absent → null. */
export function readExpParam(search) {
	const raw = new URLSearchParams(search || '').get(EXP_PARAM);
	if (raw === null) return null;
	return raw === '0' || raw === 'off' ? 'off' : 'on';
}

/** Resolve the panel flag for this load, applying any `?ktexp=` instruction. */
export function resolveExpEnabled(search, storage) {
	const instruction = readExpParam(search);
	try {
		if (instruction === 'on') {
			storage.setItem(EXP_STORAGE_KEY, '1');
			return true;
		}
		if (instruction === 'off') {
			storage.removeItem(EXP_STORAGE_KEY);
			return false;
		}
		return storage.getItem(EXP_STORAGE_KEY) === '1';
	} catch (e) {
		return instruction === 'on';
	}
}

/**
 * Build the tappable panel. The diagnostic overlay itself is click-through, so
 * the buttons carry their own pointer-events.
 */
export function buildExperimentPanel(doc, win, onResult) {
	const panel = doc.createElement('div');
	const s = panel.style;
	s.display = 'flex';
	s.flexWrap = 'wrap';
	s.gap = '4px';
	s.marginTop = '4px';
	s.pointerEvents = 'auto';

	EXPERIMENTS.forEach((exp) => {
		const b = doc.createElement('button');
		b.type = 'button';
		b.textContent = exp.key;
		const bs = b.style;
		bs.font = '11px/1 Menlo, monospace';
		bs.padding = '7px 9px';
		bs.background = '#003300';
		bs.color = '#0f0';
		bs.border = '1px solid #0f0';
		bs.borderRadius = '3px';
		b.addEventListener('click', () => {
			let result;
			try {
				result = exp.run(win);
			} catch (err) {
				result = `err:${err && err.message}`;
			}
			b.textContent = `${exp.key}:${result}`;
			if (onResult) onResult(exp.key, result);
		});
		panel.appendChild(b);
	});
	return panel;
}
